import * as React from 'react';
import { Input } from '@/components/ui/Input.jsx';
import { Table, THead, TBody, TR, TH, TD } from '@/components/ui/Table.jsx';
import { Button } from '@/components/ui/Button.jsx';
import { ChevronLeft, ChevronRight, Download, Search, ChevronUp, ChevronDown } from 'lucide-react';
import { cn } from '@/lib/cn.js';

/**
 * Generic, controlled-ish DataTable with client-side search/sort/pagination.
 *
 * columns: [{ key, header, render?, sortable?, className?, accessor?, exportValue? }]
 */
export function DataTable({
  columns,
  data = [],
  loading = false,
  searchPlaceholder = 'Search...',
  emptyMessage = 'No records found',
  pageSize = 10,
  enableSearch = true,
  enableExport = true,
  exportFilename = 'export.csv',
  onRowClick,
  rowKey = (r, i) => r.id ?? i,
  rowClassName,
  toolbar,
}) {
  const [q, setQ] = React.useState('');
  const [sort, setSort] = React.useState({ key: null, dir: 'asc' });
  const [page, setPage] = React.useState(1);

  const filtered = React.useMemo(() => {
    if (!q) return data;
    const ql = q.toLowerCase();
    return data.filter((row) =>
      columns.some((c) => {
        const v = c.accessor ? c.accessor(row) : row[c.key];
        return v != null && String(v).toLowerCase().includes(ql);
      }),
    );
  }, [data, q, columns]);

  const sorted = React.useMemo(() => {
    if (!sort.key) return filtered;
    const col = columns.find((c) => c.key === sort.key);
    if (!col) return filtered;
    const get = (r) => (col.accessor ? col.accessor(r) : r[col.key]);
    return [...filtered].sort((a, b) => {
      const va = get(a);
      const vb = get(b);
      if (va == null && vb == null) return 0;
      if (va == null) return 1;
      if (vb == null) return -1;
      if (typeof va === 'number' && typeof vb === 'number') return sort.dir === 'asc' ? va - vb : vb - va;
      return sort.dir === 'asc'
        ? String(va).localeCompare(String(vb))
        : String(vb).localeCompare(String(va));
    });
  }, [filtered, sort, columns]);

  const totalPages = Math.max(1, Math.ceil(sorted.length / pageSize));
  const safePage = Math.min(page, totalPages);
  const paged = sorted.slice((safePage - 1) * pageSize, safePage * pageSize);

  React.useEffect(() => {
    setPage(1);
  }, [q, data]);

  const toggleSort = (key) => {
    setSort((prev) => {
      if (prev.key !== key) return { key, dir: 'asc' };
      if (prev.dir === 'asc') return { key, dir: 'desc' };
      return { key: null, dir: 'asc' };
    });
  };

  const exportCsv = () => {
    const escape = (v) => {
      if (v == null) return '';
      const s = String(v).replace(/"/g, '""');
      return /[",\n]/.test(s) ? `"${s}"` : s;
    };
    const header = columns.map((c) => escape(c.header)).join(',');
    const rows = sorted.map((row) =>
      columns
        .map((c) => {
          if (c.exportValue) return escape(c.exportValue(row));
          if (c.accessor) return escape(c.accessor(row));
          return escape(row[c.key]);
        })
        .join(','),
    );
    const csv = [header, ...rows].join('\n');
    const blob = new Blob([csv], { type: 'text/csv;charset=utf-8;' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = exportFilename;
    a.click();
    URL.revokeObjectURL(url);
  };

  return (
    <div className="space-y-3">
      <div className="flex flex-wrap items-center gap-2">
        {enableSearch && (
          <div className="relative flex-1 min-w-[220px] max-w-md">
            <Search className="absolute left-2.5 top-2.5 h-4 w-4 text-slate-400" />
            <Input
              placeholder={searchPlaceholder}
              value={q}
              onChange={(e) => setQ(e.target.value)}
              className="pl-8"
            />
          </div>
        )}
        <div className="flex-1" />
        {toolbar}
        {enableExport && (
          <Button variant="outline" size="sm" onClick={exportCsv}>
            <Download className="h-4 w-4" />
            Export CSV
          </Button>
        )}
      </div>

      <Table>
        <THead>
          <TR>
            {columns.map((c) => (
              <TH key={c.key} className={cn(c.headClassName)}>
                {c.sortable ? (
                  <button
                    type="button"
                    onClick={() => toggleSort(c.key)}
                    className="inline-flex items-center gap-1 hover:text-slate-700 dark:hover:text-slate-200"
                  >
                    {c.header}
                    {sort.key === c.key &&
                      (sort.dir === 'asc' ? (
                        <ChevronUp className="h-3 w-3" />
                      ) : (
                        <ChevronDown className="h-3 w-3" />
                      ))}
                  </button>
                ) : (
                  c.header
                )}
              </TH>
            ))}
          </TR>
        </THead>
        <TBody>
          {loading ? (
            <TR>
              <TD colSpan={columns.length} className="text-center text-slate-500 py-6">
                Loading...
              </TD>
            </TR>
          ) : paged.length === 0 ? (
            <TR>
              <TD colSpan={columns.length} className="text-center text-slate-500 py-6">
                {emptyMessage}
              </TD>
            </TR>
          ) : (
            paged.map((row, i) => (
              <TR
                key={rowKey(row, i)}
                onClick={onRowClick ? () => onRowClick(row) : undefined}
                className={cn(onRowClick && 'cursor-pointer', rowClassName?.(row, i))}
              >
                {columns.map((c) => (
                  <TD key={c.key} className={c.className}>
                    {c.render ? c.render(row) : c.accessor ? c.accessor(row) : row[c.key]}
                  </TD>
                ))}
              </TR>
            ))
          )}
        </TBody>
      </Table>

      <div className="flex items-center justify-between text-sm text-slate-500">
        <div>
          {sorted.length === 0
            ? '0 records'
            : `Showing ${(safePage - 1) * pageSize + 1}–${Math.min(safePage * pageSize, sorted.length)} of ${sorted.length}`}
        </div>
        <div className="flex items-center gap-2">
          <Button variant="outline" size="sm" onClick={() => setPage((p) => Math.max(1, p - 1))} disabled={safePage <= 1}>
            <ChevronLeft className="h-4 w-4" />
          </Button>
          <span>
            Page {safePage} of {totalPages}
          </span>
          <Button variant="outline" size="sm" onClick={() => setPage((p) => Math.min(totalPages, p + 1))} disabled={safePage >= totalPages}>
            <ChevronRight className="h-4 w-4" />
          </Button>
        </div>
      </div>
    </div>
  );
}
